import React, { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin } from 'lucide-react';
import Footer from '../Sections/footer';
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import Navbar from "../components/navbar";

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchEvents() {
      try {
        setLoading(true);
        const eventsCol = collection(db, "events");
        const q = query(eventsCol, orderBy("createdAt", "desc"));
        const eventSnapshot = await getDocs(q);
        const eventList = eventSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setEvents(eventList);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchEvents();
  }, []);
  function isDatePassed(dateString) {

    const givenDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return givenDate < today;
  }

  // Full page loading component
  const FullPageLoader = () => (
    <div className="min-h-screen bg-black flex flex-col justify-center items-center">
      <div className="w-16 h-16 text-red-500 mb-6">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M13 10V3L4 14h7v7l9-11h-7z" strokeDasharray="80" strokeDashoffset="80">
            <animate attributeName="stroke-dashoffset" values="80;0;80" dur="1s" repeatCount="indefinite"></animate>
          </path>
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-white mb-2">Loading Events</h2>
      <p className="text-gray-400">Please wait while we fetch the latest events...</p>
    </div>
  );

  // Show loading screen until data is loaded
  if (loading) {
    return <FullPageLoader />;
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      {/* Header */}
      <header className="bg-black border-b border-red-500/30 py-8">
        <div className="max-w-6xl mx-auto mt-16 px-6 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            <span className="text-white drop-shadow-lg">Past</span>{" "}
            <span className="text-red-500 drop-shadow-lg">Events</span>
          </h1>
          <div className="flex justify-center mb-6">
            <div className="w-24 h-1 bg-gradient-to-r from-transparent via-red-500 to-transparent rounded-full"></div>
          </div>
          <p className="text-gray-400 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed font-light">
            Explore our successful events and workshops
          </p>
        </div>
      </header>

      {/* Events Grid */}
      <main className="py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event) => {
              const passed = isDatePassed(event.date);
              return (
                <div
                  key={event.id}
                  className="group bg-gradient-to-br from-black to-gray-900 border border-gray-700 rounded-lg overflow-hidden hover:shadow-2xl hover:shadow-red-500/20 hover:scale-105 transition-all duration-500 hover:-translate-y-2 flex flex-col"
                >

                  {/* Event Image */}
                  <div className="relative overflow-hidden h-48">
                    <img
                      src={event.imageUrl}
                      alt={`${event.title} poster`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute top-3 right-3 z-10">
                      <div className="bg-black/70 backdrop-blur-sm px-3 py-1 rounded-full border border-red-500/30">
                        <span className="text-red-400 text-xs font-medium">
                          {event.isLive ? "🔴 Live" : !passed ? "Upcoming" : "Completed"}
                        </span>
                      </div>
                    </div>

                    {/* Hover glow effect */}
                    <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  </div>

                  {/* Event Content */}
                  <div className="p-4 flex flex-col flex-grow group-hover:bg-gray-750 transition-colors duration-300">
                    <h3 className="text-lg font-bold text-white mb-2 group-hover:text-red-100 transition-colors duration-300">
                      {event.title}
                    </h3>
                    <p className="text-gray-400 text-sm mb-4 leading-relaxed group-hover:text-gray-300 transition-colors duration-300 flex-grow">
                      {event.description}
                    </p>

                    {/* Event Details */}
                    <div className="space-y-2 border-t border-gray-700 group-hover:border-red-500/30 pt-4 mb-4 transition-colors duration-300">
                      <div className="flex items-center gap-2 text-sm group-hover:bg-gray-700/30 rounded px-2 py-1 transition-all duration-300">
                        <Calendar className="w-4 h-4 text-red-400 group-hover:text-red-300 transition-colors duration-300" />
                        <span className="text-gray-300 group-hover:text-white transition-colors duration-300">{event.date}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm group-hover:bg-gray-700/30 rounded px-2 py-1 transition-all duration-300">
                        <MapPin className="w-4 h-4 text-red-400 group-hover:text-red-300 transition-colors duration-300" />
                        <span className="text-gray-300 group-hover:text-white transition-colors duration-300">{event.location}</span>
                      </div>
                      {event.type && (
                        <div className="flex items-center gap-2 text-sm group-hover:bg-gray-700/30 rounded px-2 py-1 transition-all duration-300">
                          <Clock className="w-4 h-4 text-red-400 group-hover:text-red-300 transition-colors duration-300" />
                          <span className="text-gray-300 group-hover:text-white transition-colors duration-300">{event.type}</span>
                        </div>
                      )}
                    </div>

                    {/* Registration Link */}
                    {passed ? (
                      <button
                        className="w-full py-2 px-4 rounded-full font-bold bg-gray-800 text-gray-500 cursor-not-allowed text-sm"
                        disabled
                      >
                        Completed
                      </button>
                    ) : (
                      <button
                        className="w-full py-2 px-4 rounded-full font-bold bg-red-700 hover:bg-red-600 text-white transition-all duration-300 text-sm"
                        onClick={() => {
                          if (event.registrationLink) {
                            window.open(event.registrationLink, "_blank", "noopener,noreferrer");
                          }
                        }}
                      >
                        Register Now
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default EventsPage;